/**
 * The picture map's extension points (spec 026, for 027). The Pictures view,
 * the post-draft handoff and the studio strip render each hook where named
 * and nothing when it is absent. Spec 026 mounts no provider, so no Generate
 * control appears; 027 mounts one and calls `storyStore.fillPicture`.
 */
import React, { createContext, useContext, type ReactNode } from 'react';
import type { PictureSlot } from '../../../../Shared/src/@types';
import type { ImageOwnerRef } from '../../../../Shared/src/images';

export interface PictureExtensions {
  /** The first row of the Pictures view; `owner` is the story or (spec 030) the scene, `storyId` set for a story. */
  topRow?: (ctx: { owner: ImageOwnerRef; storyId?: number }) => ReactNode;
  /** On a slot card, before Upload. */
  slotAction?: (slot: PictureSlot) => ReactNode;
  /** The side panel, above "Upload an image". */
  panelAction?: (slot: PictureSlot) => ReactNode;
  /** The handoff, after "Fill pictures now". */
  allAction?: (needed: PictureSlot[]) => ReactNode;
  /** A studio strip row, before "Change picture". */
  stripAction?: (sprite: { id: string; name: string }, slot?: PictureSlot) => ReactNode;
  /** After a Reset (027 D-33) applied: the slots it reset, so the extension forgets what it kept for them. */
  slotsReset?: (slotIds: string[]) => void;
}

const NO_EXTENSIONS: PictureExtensions = {};

export const PictureExtensionsContext: React.Context<PictureExtensions> = createContext<PictureExtensions>(NO_EXTENSIONS);

export function usePictureExtensions(): PictureExtensions {
  return useContext(PictureExtensionsContext);
}
